import { DialogTitle } from '@headlessui/react'
import { useQueryClient } from '@tanstack/react-query'
import PropTypes from 'prop-types'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useNotificationContext, usePartnerContext } from '../../context'
import {
  invalidatePricingUsage,
  useTalentRecoverGuard
} from '../../features/pricing/hooks'
import { mutation } from '../../graphql'
import { useCreateLog } from '../../hooks/use-create-log'
import Button from '../tailwind/Button'
import InfoCallout from '../tailwind/InfoCallout'
import Modal from '../tailwind/Modal'

const RecoverSpacesAction = ({ spaces, updateHandler, open, setOpen }) => {
  const { t } = useTranslation()
  const { success } = useNotificationContext()
  const { partner } = usePartnerContext()
  const queryClient = useQueryClient()

  const { createSpaceHistoryLog } = useCreateLog()
  const { checkRecover } = useTalentRecoverGuard()

  const [isLoading, setIsLoading] = useState(false)
  // Größe der Auswahl beim Block, der Hinweis nennt die ausgewählten
  // Talente. Das delta zählt dagegen neue Personen und ist kleiner, sobald
  // eine Zeile einer schon gezählten Person oder Beispieldaten dabei sind.
  const [blockedCount, setBlockedCount] = useState(null)

  // Jedes Öffnen beginnt ohne den Hinweis vom letzten Mal
  useEffect(() => {
    if (open) setBlockedCount(null)
  }, [open])

  const recoverSpaces = async () => {
    setIsLoading(true)

    // Defensiv noch einmal frisch zählen, die Seite hat schon vor dem Öffnen
    // geprüft. Seitdem kann ein Talent dazugekommen sein. Der Hinweis bleibt
    // hier im Modal, einen zweiten Dialog darüber stapelt HeadlessUI 2.1
    // nicht. Außerhalb von enforced gibt der Guard sofort frei, ein
    // Zählfehler ebenso (fail-open).
    const check = await checkRecover(spaces)
    if (!check.allowed) {
      setBlockedCount(spaces.length)
      setIsLoading(false)
      return
    }

    try {
      const event = { context: 'SYSTEM', status: 'RECOVERED' }

      for (const space of spaces) {
        const { id: space_id, career_id } = space
        const input = { id: space_id, soft_deleted: null }

        createSpaceHistoryLog({ space_id, career_id, ...event })
        await mutation({ mutation: 'updateSpace', input })
      }

      // Wiederhergestellte Talente belegen wieder einen Platz und verschieben
      // den Rang. Nur bei durchgesetztem Limit, sonst bleibt alles wie heute.
      if (check.enforced) {
        invalidatePricingUsage({
          queryClient,
          partnerId: partner?.id,
          resource: 'talentStorage'
        })
      }

      await updateHandler()

      success(t('recover_talents.recover_space_success'))
      setIsLoading(false)
      setOpen(false)
    } catch (err) {
      console.error(err)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Modal open={open} setOpen={() => setOpen(false)} size='xl'>
      <div className='p-4'>
        <DialogTitle className='text-center text-2xl font-bold text-gray-900'>
          {t('recover_talents.recover_action')}
        </DialogTitle>
        <span className='mt-3 block text-center text-base text-gray-900'>
          {t('recover_talents.recover_description')}
        </span>
        {blockedCount !== null && (
          <div role='alert' className='mt-6'>
            <InfoCallout tone='critical'>
              {t('pricing.limit.talent_storage.text_recover', {
                count: blockedCount
              })}{' '}
              {t('pricing.limit.talent_storage.text_info')}
            </InfoCallout>
          </div>
        )}
        <div className='mt-8 flex flex-row gap-3'>
          <Button.SecondaryXL
            fullWidth
            text={t('recover_talents.recover_cancel_action')}
            onClick={() => setOpen(false)}
          />
          <Button.PrimaryXL
            fullWidth
            text={
              spaces.length === 1
                ? t('recover_talents.recover_action_single', {
                    count: spaces.length
                  })
                : t('recover_talents.recover_action_multiple', {
                    count: spaces.length
                  })
            }
            onClick={recoverSpaces}
            isLoading={isLoading}
          />
        </div>
      </div>
    </Modal>
  )
}

RecoverSpacesAction.propTypes = {
  spaces: PropTypes.array.isRequired,
  updateHandler: PropTypes.func,
  open: PropTypes.bool.isRequired,
  setOpen: PropTypes.func.isRequired
}

RecoverSpacesAction.defaultProps = {
  updateHandler: () => undefined
}

export default RecoverSpacesAction
